import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

import SalesPageHeader from '../components/sales/SalesPageHeader';
import { useSalesEmbedded } from '../hooks/useSalesEmbedded';
import PortalBackground from '../components/PortalBackground';

/**
 * Draw tiers by contract price. A price matches the tier where
 * min <= price < max (last tier is open-ended). Percentages per tier sum to 100.
 */
const DRAW_TIERS = [
  {
    draws: 5,
    label: 'Up to $150k',
    min: 0,
    max: 150000,
    schedule: [
      { milestone: 'Contract Signing', percentage: 30 },
      { milestone: 'Demo Start', percentage: 25 },
      { milestone: 'Delivery of Windows', percentage: 23 },
      { milestone: 'Substantial Completion', percentage: 17 },
      { milestone: 'Final Walk-through', percentage: 5 },
    ],
  },
  {
    draws: 6,
    label: '$150k - $250k',
    min: 150000,
    max: 250000,
    schedule: [
      { milestone: 'Contract Signing', percentage: 25 },
      { milestone: 'Demo Start', percentage: 20 },
      { milestone: 'MEP Rough-In Start', percentage: 18 },
      { milestone: 'Countertop Install', percentage: 17 },
      { milestone: 'Substantial Completion', percentage: 15 },
      { milestone: 'Final Walk-through', percentage: 5 },
    ],
  },
  {
    draws: 7,
    label: '$250k - $500k',
    min: 250000,
    max: 500000,
    schedule: [
      { milestone: 'Contract Signing', percentage: 25 },
      { milestone: 'Demo Start', percentage: 18 },
      { milestone: 'Framing Package Delivery', percentage: 16 },
      { milestone: 'MEP Rough-In Start', percentage: 15 },
      { milestone: 'Countertop Install', percentage: 14 },
      { milestone: 'Substantial Completion', percentage: 7 },
      { milestone: 'Final Walk-through', percentage: 5 },
    ],
  },
  {
    draws: 8,
    label: '$500k - $1M',
    min: 500000,
    max: 1000000,
    schedule: [
      { milestone: 'Contract Signing', percentage: 25 },
      { milestone: 'Demo Start', percentage: 19 },
      { milestone: 'Framing Package Delivery', percentage: 13 },
      { milestone: 'MEP Rough-In Start', percentage: 12 },
      { milestone: 'Cabinet Delivery', percentage: 10 },
      { milestone: 'Countertop Install', percentage: 9 },
      { milestone: 'Substantial Completion', percentage: 7 },
      { milestone: 'Final Walk-through', percentage: 5 },
    ],
  },
  {
    draws: 9,
    label: '$1M+',
    min: 1000000,
    max: Infinity,
    schedule: [
      { milestone: 'Contract Signing', percentage: 28 },
      { milestone: 'Interior Demo Completion', percentage: 17 },
      { milestone: 'MEP Rough-In Completion', percentage: 15 },
      { milestone: 'Hung Drywall Completion', percentage: 12 },
      { milestone: 'Cabinet Delivery', percentage: 9 },
      { milestone: 'Countertop Install', percentage: 8 },
      { milestone: 'MEP Trim-Out', percentage: 5 },
      { milestone: 'Substantial Completion', percentage: 4 },
      { milestone: 'Punch List Completion', percentage: 2 },
    ],
  },
];

const findTier = (price) =>
  DRAW_TIERS.find((t) => price >= t.min && price < t.max) || DRAW_TIERS[DRAW_TIERS.length - 1];

const money = (value) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const BuildPaymentCalculator = ({ embedded: embeddedProp }) => {
  const embedded = useSalesEmbedded(embeddedProp);
  const { darkMode } = useTheme();

  const [contractPrice, setContractPrice] = useState('');

  const price = parseFloat(contractPrice);
  const tier = price > 0 ? findTier(price) : null;

  const card = `rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm shadow-md border ${
    darkMode ? 'border-gray-700' : 'border-gray-100'
  } p-6`;

  return (
    <div className={`${embedded ? '' : 'relative min-h-screen p-6'} text-gray-900 dark:text-white`}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {!embedded && <SalesPageHeader title="Build Payment Calculator" />}

      {/* Contract price + payment schedule in one card */}
      <div className={`${card} mb-6`}>
        <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Payment Schedule
        </h3>
        <div className="max-w-xl mx-auto">
          <label className={`block text-xs uppercase tracking-wider font-medium mb-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Contract Price
          </label>
          <div className="relative">
            <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
            <input
              type="number"
              value={contractPrice}
              onChange={(e) => setContractPrice(e.target.value)}
              className={`w-full pl-8 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Contract price"
              min="0"
              step="1000"
            />
          </div>

          {tier ? (
            <div className="mt-6">
              <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {tier.schedule.map((draw) => (
                  <div key={draw.milestone} className="flex items-center justify-between gap-3 py-2.5">
                    <span className={`text-sm min-w-0 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Due upon {draw.milestone}
                    </span>
                    <span className="flex items-center gap-3 shrink-0">
                      <span className={`text-xs w-9 text-right ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {draw.percentage}%
                      </span>
                      <span className={`text-sm font-semibold w-28 text-right ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        ${money((price * draw.percentage) / 100)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <div
                className={`flex items-center justify-between gap-3 py-2.5 mt-1 border-t-2 ${
                  darkMode ? 'border-gray-600' : 'border-gray-300'
                }`}
              >
                <span className={`text-sm font-bold tracking-tight text-gray-900 dark:text-white`}>Total</span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs w-9 text-right ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>100%</span>
                  <span className="text-sm font-bold w-28 text-right text-red-600 dark:text-red-400">
                    ${money(price)}
                  </span>
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className={`text-2xl font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'} mb-2`}>
                Enter contract price
              </div>
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                to see the payment schedule
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Draw Structure - always visible, current tier highlighted */}
      <div className={card}>
        <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Draw Structure
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {DRAW_TIERS.map((t) => {
            const isCurrent = tier && tier.draws === t.draws;
            return (
              <div
                key={t.draws}
                className={`rounded-lg border p-4 ${
                  isCurrent
                    ? `border-red-500 ring-1 ring-red-500 ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`
                    : darkMode
                    ? 'border-gray-700 bg-gray-900/40'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="text-center mb-3">
                  <p className={`text-base font-bold ${isCurrent ? 'text-red-600 dark:text-red-400' : darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t.draws} Draws
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t.label}</p>
                </div>
                <div className={`divide-y ${darkMode ? 'divide-gray-700/60' : 'divide-gray-200'}`}>
                  {t.schedule.map((draw) => (
                    <div key={draw.milestone} className="flex items-center justify-between gap-2 py-1.5">
                      <span className={`text-xs min-w-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {draw.milestone}
                      </span>
                      <span className={`text-xs font-semibold shrink-0 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {draw.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
};

export default BuildPaymentCalculator;
